import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonContainer, LocationButton, LocationText, Header } from './LocationFilter.styles';
import { LOCATION_SEARCH_MODAL_OPEN_EVENT } from '../../../constants';
import LocationIcon from '../../../assets/icons/LocationIcon';
import { COLORS } from '../../../styles/variables';
import { LocationSearchData, LocationSearchDataByRadius } from '../../../types';
import { PARTNER_ID } from '../../../constants/environment';

interface LocationProps {
  userSelectedLocation: LocationSearchData;
  market: string;
  isLaptop: boolean;
}

const LocationFilter: React.FC<LocationProps> = ({ userSelectedLocation, market, isLaptop }) => {
  const { t } = useTranslation();

  const onLocationSearchOpen = useCallback(() => {
    const event = new CustomEvent(LOCATION_SEARCH_MODAL_OPEN_EVENT);
    document.dispatchEvent(event);
  }, []);

  const getLocationText = useMemo(() => {
    if (!userSelectedLocation) return '';
    const location = userSelectedLocation;
    if (!location?.dealershipList?.length) {
      return t('vlp.filters.dealersBand.noLocation');
    }
    if (location.mode === 'BY_RADIUS' && location.selectedUserLocation) {
      const { name, formattedAddress } = location.selectedUserLocation.feature;
      const { radius } = location as LocationSearchDataByRadius;
      const radiusUnit = market === 'CAN' ? 'km' : 'miles';
      return t('vlp.filters.dealersBand.radiusSearch', {
        userLocationName: name || formattedAddress,
        selectedRadius: radius,
        radiusUnit,
      });
    }
    if (location.mode === 'BY_DEALERS') {
      if (location.dealershipList.length === 1) {
        return `${location.dealershipList[0].name}`;
      }
      if (location.dealershipList.length > 1)
        return t('vlp.filters.dealersBand.multiDealer', {
          selectedDealerCount: location.dealershipList.length,
        });
    }
    return '';
  }, [market, t, userSelectedLocation]);

  const disableLocationFilter = !!(PARTNER_ID && PARTNER_ID?.length);

  // Migrate to Unified Later
  return (
    <>
      <ButtonContainer disabled={disableLocationFilter}>
        <LocationButton
          disabled={disableLocationFilter}
          onClick={onLocationSearchOpen}
          isLaptop={isLaptop}
        >
          <Header>
            <LocationIcon color={disableLocationFilter ? COLORS.grey60 : COLORS.black} />
            {isLaptop && <div>{t('vlp.filters.dealersBand.locationLabel')}</div>}
          </Header>
          <LocationText disabled={disableLocationFilter}>{getLocationText}</LocationText>
        </LocationButton>
      </ButtonContainer>
    </>
  );
};

export default LocationFilter;
